// @file Surface wish arrangement store
import { getAuthorDisplayName } from '@@/bits/author'
import { isAppUsing } from '@@/bits/flip'
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import {
  BLANK_GROUP,
  getCustomPropIdFromWishGroupBy,
  getOrderedColorGroupsWithTitle,
  isGroupByCustomProp,
} from '@@/bits/surface_posts_grouping_helper'
import type { ManualSortBy } from '@@/bits/surface_settings_helper'
import { ManualSortByArray } from '@@/bits/surface_settings_helper'
import { WallWishGroupByTypes, WallWishGroupByValue, WallWishSortByTypes } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type {
  WallCustomPostSingleSelectProperty,
  WallGroupPost,
  WallGroupSingleSelect,
  WallWishGroupBy,
  WallWishGroupByCustomProp,
  WishArrangement,
} from '@@/types'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import { SortByTypes } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useSurfaceWishArrangementStore = defineStore('surfaceWishArrangement', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()

  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
  const surfacePostGroupByStore = useSurfacePostGroupByStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

  const originalWishArrangement = computed<WishArrangement | undefined>(
    () => surfaceVuexStore?.state.wall.wish_arrangement,
  )
  const previewWishArrangement = computed<WishArrangement | undefined>(
    () => surfaceSettingsStore.previewAttributes.wish_arrangement,
  )

  const wishGroupBy = computed<WallWishGroupBy>(() => {
    if (!surfaceStore.canUseSections) return WallWishGroupByTypes.None
    const groupByValue = surfaceSettingsStore.isPreviewing
      ? surfaceSettingsStore.wishGroupBy
      : surfaceVuexStore?.getters.wishGroupBy
    if (surfaceStore.isShelf && groupByValue === WallWishGroupByTypes.None) return WallWishGroupByTypes.Section
    return groupByValue
  })
  const isSectionedAndNonmanualSorted = computed<boolean>(
    () => !areWishesManuallySorted.value && surfaceStore.isSectioned,
  )
  const isNotSectionedAndNonmanualSorted = computed<boolean>(
    () => !areWishesManuallySorted.value && !surfaceStore.isSectioned,
  )
  const isSectionable = computed<boolean>(() => {
    if (!isAppUsing('groupPostsV2') && surfaceStore.isShelf) return true
    return surfaceStore.wallAttributes.wish_arrangement?.is_sectionable ?? false
  })
  const originalWishSortBy = computed<WallWishSortByTypes>(() => surfaceVuexStore?.getters.wishSortBy)
  const previewWishSortBy = computed<WallWishSortByTypes>(() => surfaceSettingsStore.wishSortBy)
  const hasWishSortByChanged = computed(() => previewWishSortBy.value !== originalWishSortBy.value)
  const defaultWishSortBy = computed<SortByTypes.ManualNewPostsLast | SortByTypes.ManualNewPostsFirst>(() => {
    if (surfaceStore.isTimeline || surfaceStore.isShelf) return SortByTypes.ManualNewPostsLast
    return SortByTypes.ManualNewPostsFirst
  })
  const areWishesManuallySorted = computed<boolean>(() =>
    ManualSortByArray.includes(originalWishSortBy.value as ManualSortBy),
  )

  /**
   * Determines whether wishes are currently not grouped (grouped by None).
   * Returns true when no grouping is applied, and false when wishes are
   * grouped by section, author, or color.
   */
  const isGroupByNone = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.None
  })

  const isGroupBySection = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.Section
  })

  const isGroupByColor = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.Color
  })

  const isGroupByAuthor = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.Author
  })

  const isGroupBySingleSelect = computed(() => {
    return isGroupByCustomProp(wishGroupBy.value)
  })

  const customPropIdFromWishGroupBy = computed((): WallCustomPostSingleSelectProperty['id'] | null => {
    if (!isGroupBySingleSelect.value) return null
    return getCustomPropIdFromWishGroupBy(wishGroupBy.value)
  })

  const activeSingleSelectGroupByProperty = computed<WallCustomPostSingleSelectProperty | undefined>(() => {
    if (!isGroupBySingleSelect.value) return undefined
    return getSingleSelectPropertyForWishGroupBy(wishGroupBy.value)
  })

  // Given a groupBy setting value, returns the associated single select property if applicable.
  function getSingleSelectPropertyForWishGroupBy(
    groupBy: WallWishGroupBy,
  ): WallCustomPostSingleSelectProperty | undefined {
    if (!isGroupByCustomProp(groupBy)) return undefined
    const customPropId = getCustomPropIdFromWishGroupBy(groupBy)
    if (customPropId == null) return undefined
    return surfacePostPropertiesStore.wallSingleSelectPropsByCustomPropertyId[customPropId]
  }

  const sortedGroups = computed<WallGroupPost[]>(() => {
    if (surfaceShareLinksStore.isBreakout) {
      return surfaceShareLinksStore.breakoutLinkGroups
    }

    switch (wishGroupBy.value) {
      case WallWishGroupByTypes.Section:
        return surfaceSectionsStore.sortedSections
      case WallWishGroupByTypes.Color:
        return getOrderedColorGroupsWithTitle({ isLightColorScheme: surfaceStore.colorScheme === 'light' })
      case WallWishGroupByTypes.Author:
        return Object.values(surfaceUserContributorsStore.postAuthorsByHashId)
          .map((author) => {
            return {
              id: author.hashid ? removeHashidPrefix(author.hashid) : '',
              title: getAuthorDisplayName(author),
              avatar: author.avatar,
            }
          })
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      case WallWishGroupByTypes.None:
        return [BLANK_GROUP]
      default: {
        // Group by single select custom property
        if (customPropIdFromWishGroupBy.value == null) return []

        const singleSelectProperty = surfacePostGroupByStore.currentSingleSelectProperty
        if (singleSelectProperty == null) return []

        const singleSelectOptions = singleSelectProperty.selection_options

        const singleSelectGroups: WallGroupSingleSelect[] = singleSelectOptions.map((option) => ({
          id: option.id ?? '',
          title: option.name,
        }))

        return [...singleSelectGroups, { id: WallWishGroupByValue.NoValue, title: __('No value') }]
      }
    }
  })

  const isWishGroupedBySingleSelectProperty = (propertyId: WallGroupSingleSelect['id']): boolean => {
    return isGroupBySingleSelect.value && propertyId === customPropIdFromWishGroupBy.value
  }

  const getWishGroupByValueFromCustomPropId = (
    singleSelectId: WallCustomPostSingleSelectProperty['id'],
  ): WallWishGroupByCustomProp => {
    return `custom_prop:${singleSelectId ?? ''}`
  }

  function updateWishGroupBy(groupBy: WallWishGroupBy): void {
    if (wishGroupBy.value === groupBy) return

    surfaceSettingsStore.startWallAttributesPreview()

    const groupByCustomPropId = getCustomPropIdFromWishGroupBy(groupBy)
    const isWallSortedBySelectedCustomProp =
      groupByCustomPropId != null && previewWishSortBy.value?.startsWith(`custom_prop:${groupByCustomPropId}:`)

    const wishArrangement = {
      default_section_id: surfaceSectionsStore.defaultSectionId,
      group_by: groupBy === WallWishGroupByTypes.None ? null : groupBy,
      is_sectionable: groupBy === WallWishGroupByTypes.Section,
      sort_by: isWallSortedBySelectedCustomProp ? WallWishSortByTypes.ManualNewPostsLast : previewWishSortBy.value,
    }

    surfaceSettingsStore.updatePreviewAttributesAndSaveSettings({
      wish_arrangement: wishArrangement,
    })
  }

  return {
    isNotSectionedAndNonmanualSorted,
    isSectionedAndNonmanualSorted,
    wishGroupBy,
    defaultWishSortBy,
    isGroupByNone,
    isGroupBySection,
    isGroupByColor,
    isGroupByAuthor,

    isGroupBySingleSelect,
    isWishGroupedBySingleSelectProperty,
    customPropIdFromWishGroupBy,
    getSingleSelectPropertyForWishGroupBy,
    activeSingleSelectGroupByProperty,

    sortedGroups,
    originalWishSortBy,
    previewWishSortBy,
    originalWishArrangement,
    previewWishArrangement,
    hasWishSortByChanged,
    areWishesManuallySorted,
    isSectionable,

    getWishGroupByValueFromCustomPropId,
    updateWishGroupBy,
  }
})
